import React, { useState, useEffect, useRef } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import backk from './images/backk.png'; // Replace with the correct path

const GoogleLoginComponent = () => {
  const [email, setEmail] = useState('');
  const [gDriveLink, setGDriveLink] = useState('');
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  let interval;
  let stream;

  const styles = {
    container: {
      background: 'linear-gradient(90deg, #fff7ad, #ffa9f9)',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Arial, sans-serif',
    },
    heading: {
      color: '#E11584',
      fontSize: '2rem',
      marginBottom: '1rem',
    },
    text: {
      color: '#E11584',
      fontSize: '1.2rem',
    },
    video: {
      width: '50%',
      marginTop: '1rem',
    },
    message: {
      marginTop: '1rem',
      fontSize: '1rem',
      color: '#333',
    },
    backButton: {
      backgroundColor: 'transparent',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      marginTop: '1rem',
    },
    backImage: {
      width: '30px',
      height: '40px',
    },
  };

  const login = useGoogleLogin({
    onSuccess: async (response) => {
      const token = response.access_token;
      sessionStorage.setItem('googleToken', token);
      alert('Google login successful!');

      try {
        const profileResponse = await axios.get(
          `https://www.googleapis.com/oauth2/v1/userinfo?alt=json&access_token=${token}`
        );
        const userEmail = profileResponse.data.email;
        setEmail(userEmail);
        sessionStorage.setItem('userEmail', userEmail);
        fetchGDriveLink(userEmail);
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    },
    onError: () => {
      console.error('Login Failed');
    },
    scope: 'https://www.googleapis.com/auth/drive.file',
  });

  const fetchGDriveLink = async (userEmail) => {
    try {
      const response = await axios.post('https://www.d-rosa.ai/get-gdrivelink', { email: userEmail });
      setGDriveLink(response.data.gDriveLink);
    } catch (error) {
      console.error('Error fetching GDriveLink:', error);
      setMessage('Failed to fetch Google Drive link.');
    }
  };

  useEffect(() => {
    const savedEmail = sessionStorage.getItem('userEmail');
    const savedToken = sessionStorage.getItem('googleToken');

    if (savedEmail && savedToken) {
      setEmail(savedEmail);
      fetchGDriveLink(savedEmail);
    } else {
      login();
    }
  }, [login]);

  const startRecording = () => {
    if (!stream || isRecording) return;

    const options = { mimeType: 'video/webm; codecs=vp8,opus' };
    const recorder = new MediaRecorder(stream, options);

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        setRecordedChunks((prev) => [...prev, event.data]);
      }
    };

    recorder.onstop = async () => {
      if (recordedChunks.length > 0) {
        const videoBlob = new Blob(recordedChunks, { type: 'video/webm' });
        if (videoBlob.size > 0) {
          await uploadToDrive(videoBlob);
        } else {
          setMessage('Recorded video is empty. Skipping upload.');
        }
        setRecordedChunks([]);
      } else {
        setMessage('No video data to upload.');
      }
    };

    recorder.start();
    setMediaRecorder(recorder);
    setIsRecording(true);

    setTimeout(() => {
      stopRecording();
    }, 5000); // Stop after 5 seconds
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    const startCamera = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        video.srcObject = stream;
        video.play();

        video.addEventListener('loadedmetadata', () => {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
        });

        captureAndSendImage();
        interval = setInterval(captureAndSendImage, 5000); // Capture image every 5 seconds
      } catch (error) {
        console.error('Error accessing camera:', error);
      }
    };

    const stopCamera = () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      if (interval) {
        clearInterval(interval);
      }
    };

    const captureAndSendImage = async () => {
      try {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataURL = canvas.toDataURL('image/jpeg');
        const blob = dataURLToBlob(dataURL);
        const formData = new FormData();
        formData.append('image', blob, 'image.jpg');
        formData.append('path', `/var/www/myflaskapp/flask_static/${email}/RosaS`);

        const faceapiResponse = await fetch('https://www.d-rosa.ai/FACEAPI', {
          method: 'POST',
          body: formData,
        });
        const faceapiResult = await faceapiResponse.json();
        setMessage(faceapiResult.message);

        if (faceapiResult.message === 'Face detected' && !isRecording) {
          startRecording();
        } else if (faceapiResult.message === 'No face detected') {
          setMessage('No face detected');
        }
      } catch (error) {
        console.error('Error capturing or sending image:', error);
      }
    };

    const dataURLToBlob = (dataURL) => {
      const byteString = atob(dataURL.split(',')[1]);
      const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      return new Blob([ab], { type: mimeString });
    };

    startCamera();

    return () => {
      stopCamera();
      stopRecording();
    };
  }, [email, mediaRecorder, isRecording]);

  const uploadToDrive = async (blob) => {
    setIsUploading(true);

    try {
      const metadata = {
        name: 'recorded_video.webm',
        mimeType: 'video/webm',
        parents: [gDriveLink],
      };

      const sessionRes = await axios.post(
        'https://www.googleapis.com/upload/drive/v3/files?uploadType=resumable',
        metadata,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('googleToken')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const uploadUrl = sessionRes.headers.location;

      await axios.put(uploadUrl, blob, {
        headers: {
          'Content-Type': 'video/webm',
          'Content-Length': blob.size,
        },
      });

      setMessage('Video uploaded successfully!');
    } catch (error) {
      console.error('Error uploading file:', error.response?.data || error.message);
      setMessage('Failed to upload video.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleBackClick = () => {
    // Define your back button logic here
    console.log('Back button clicked');
    window.history.back();
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.heading}>Face Detection Screen</h1>
      {email ? (
        <>
          <p style={styles.text}>Signed in as: {email}</p>
          {gDriveLink ? <p style={styles.message}>Google Drive Link: {gDriveLink}</p> : <p>Loading GDriveLink...</p>}
        </>
      ) : (
        <p>Loading...</p>
      )}
      <video ref={videoRef} style={styles.video} />
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <p style={styles.message}>{message}</p>
      <button style={styles.backButton} onClick={handleBackClick}>
        <img src={backk} alt="Back" style={styles.backImage} />
        Go Back
      </button>
    </div>
  );
};

export default GoogleLoginComponent;
